import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Card,
    Descriptions,
    Table,
    Button,
    Space,
    Divider,
    Typography,
    notification,
    Row,
    Col,
    Statistic,
    Alert,
    Tag
} from "antd";
import {
    ArrowLeftOutlined,
    PrinterOutlined
} from "@ant-design/icons";
import { salesService } from "../../services/salesService";

const { Title, Text } = Typography;
const { Column } = Table;

const SalesDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchInvoiceDetails();
    }, [id]);

    const fetchInvoiceDetails = async () => {
        try {
            setLoading(true);
            const response = await salesService.getSalesInvoice(id);
            if (response.success) {
                setInvoice(response.data);
            }
        } catch (error) {
            notification.error({
                message: "Error",
                description:
                    error.response?.data?.error ||
                    "Failed to fetch sales invoice details",
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = async () => {
        try {
            // Trigger download by opening the URL in current window or fetching blob
            // Best way for file download with auth/headers is usually a direct link if public or via blob if auth needed
            // Here, we have no strict auth yet on this route, so window.open or link click works.
            // But to define filename cleanly, let's use a fetch blob method or just open.

            // Assuming the backend is at localhost:5000/api
            // We should use the service URL.
            // But since salesService is axios based, let's use it to download blob.

            const response = await salesService.exportSalesInvoice(id);
            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Invoice_${invoice?.invoiceNumber || id}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Download failed", error);
            notification.error({ message: "Failed to download invoice" });
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!invoice) {
        return (
            <Card>
                <Alert
                    message="Invoice Not Found"
                    type="error"
                    showIcon
                    action={
                        <Button onClick={() => navigate("/sales")}>
                            Back to Sales
                        </Button>
                    }
                />
            </Card>
        );
    }

    return (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Card>
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Space>
                                <Button
                                    icon={<ArrowLeftOutlined />}
                                    onClick={() => navigate("/sales")}
                                >
                                    Back
                                </Button>
                                <div>
                                    <Title level={3} style={{ margin: 0 }}>
                                        Sales Invoice #{invoice.invoiceNumber}
                                    </Title>
                                    <Text type="secondary">
                                        {new Date(invoice.invoiceDate).toLocaleDateString()}
                                    </Text>
                                </div>
                            </Space>
                        </Col>
                        <Col>
                            <Button icon={<PrinterOutlined />} type="primary" onClick={handlePrint}>
                                Print Invoice
                            </Button>
                        </Col>
                    </Row>
                </Space>
            </Card>

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={16}>
                    <Card title="Invoice Details">
                        <Descriptions column={2} bordered>
                            <Descriptions.Item label="Invoice Number">
                                <strong>{invoice.invoiceNumber}</strong>
                            </Descriptions.Item>
                            <Descriptions.Item label="Invoice Date">
                                {new Date(invoice.invoiceDate).toLocaleDateString()}
                            </Descriptions.Item>

                            <Descriptions.Item label="Issued By (Company)" span={2}>
                                <Space>
                                    <Text strong>{invoice.company?.companyName}</Text> (GST: {invoice.company?.gstn})
                                </Space>
                            </Descriptions.Item>

                            <Descriptions.Item label="Customer" span={2}>
                                <strong>{invoice.receiver?.customerName}</strong>
                                <br />
                                {invoice.receiver?.customerAddress}
                                <br />
                                GSTN: {invoice.receiver?.gstn || 'N/A'}
                            </Descriptions.Item>

                            <Descriptions.Item label="Contract Amount" span={1}>
                                <strong>₹{Math.round(invoice.contractAmount || 0)}</strong>
                            </Descriptions.Item>
                            <Descriptions.Item label="PO / Contract Ref" span={1}>
                                {invoice.poNumber || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Consumer Number" span={2}>
                                <Tag color="orange">{invoice.consumerNo || 'N/A'}</Tag>
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider>Items</Divider>
                        <Table
                            dataSource={invoice.items || []}
                            rowKey="id"
                            pagination={false}
                            size="small"
                        >
                            <Column
                                title="Description"
                                dataIndex="description"
                                key="description"
                                render={(text, record) => {
                                    return (
                                        <Space direction="vertical" size={0}>
                                            <Text strong>{text || record.item?.itemName || 'N/A'}</Text>
                                            {record.natureKey && <Tag color="blue" style={{ fontSize: '10px', marginTop: '4px' }}>{record.natureKey.toUpperCase()}</Tag>}
                                        </Space>
                                    );
                                }}
                            />
                            <Column
                                title="Quantity"
                                dataIndex="quantity"
                                key="quantity"
                                align="right"
                            />
                            <Column
                                title="Rate"
                                dataIndex="rate"
                                key="rate"
                                align="right"
                                render={(rate) => `₹${Math.round(rate || 0)}`}
                            />
                            <Column
                                title="Total"
                                dataIndex="totalAmount"
                                key="totalAmount"
                                align="right"
                                render={(amount, record) => (
                                    <Space direction="vertical" align="end" size={0}>
                                        <strong>₹{Math.round(amount || 0)}</strong>
                                        <Text type="secondary" style={{ fontSize: '10px' }}>
                                            Incl. GST ({record.gstRate}%)
                                        </Text>
                                    </Space>
                                )}
                            />
                        </Table>

                        {invoice.amountInWords && (
                            <>
                                <Divider>Amount in Words</Divider>
                                <Text>{invoice.amountInWords}</Text>
                            </>
                        )}
                    </Card>
                </Col>

                <Col xs={24} lg={8}>
                    <Card title="Summary">
                        <Space direction="vertical" size="large" style={{ width: "100%" }}>
                            <Statistic
                                title="Total Taxable"
                                value={Math.round(invoice.totalBeforeTax || 0)}
                                prefix="₹"
                            />
                            <Statistic
                                title="Total Tax"
                                value={Math.round(invoice.totalTax || (invoice.items || []).reduce((acc, item) => acc + (parseFloat(item.taxAmount || 0)), 0))}
                                prefix="₹"
                            />
                            <Divider />
                            <Statistic
                                title="Total After Tax"
                                value={Math.round(invoice.totalAfterTax || 0)}
                                prefix="₹"
                                valueStyle={{ color: "#52c41a", fontSize: 24 }}
                            />
                        </Space>
                    </Card>

                    {invoice.taxDetails && invoice.taxDetails.length > 0 && (
                        <Card title="Tax Details" style={{ marginTop: 16 }}>
                            <Descriptions column={1} size="small">
                                <Descriptions.Item label="CGST">
                                    ₹{Math.round(invoice.taxDetails[0].cgstAmount || 0)}
                                </Descriptions.Item>
                                <Descriptions.Item label="SGST">
                                    ₹{Math.round(invoice.taxDetails[0].sgstAmount || 0)}
                                </Descriptions.Item>
                                <Descriptions.Item label="IGST">
                                    ₹{Math.round(invoice.taxDetails[0].igstAmount || 0)}
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    )}
                </Col>
            </Row>
        </Space>
    );
};

export default SalesDetail;
